import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { registerBusiness } from '../services/businessService';
import './BusinessRegistration.css';

const STEPS = [
  { key: 'practice', label: 'Practice Info' },
  { key: 'owner', label: 'Owner Info' },
  { key: 'branding', label: 'Branding' },
  { key: 'plan', label: 'Choose Plan' }
];

const BUSINESS_TYPES = [
  { value: 'physical_therapy', label: 'Physical Therapy' },
  { value: 'nutrition', label: 'Nutrition / Dietetics' },
  { value: 'wellness', label: 'Wellness Center' },
  { value: 'fitness', label: 'Fitness / Gym' },
  { value: 'other', label: 'Other' }
];

const SPECIALIZATIONS = [
  'Sports Rehab', 'Post-Op Recovery', 'Chronic Pain', 'Pediatric',
  'Geriatric', 'Weight Management', 'Sports Nutrition', 'Clinical Nutrition',
  'Strength & Conditioning', 'Group Fitness', 'Yoga / Pilates', 'Mental Health',
  'Occupational Therapy', 'Cardiac Rehab', 'Prenatal / Postnatal'
];

export default function BusinessRegistration() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);

  const [practice, setPractice] = useState({
    name: '',
    businessType: '',
    description: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    specializations: []
  });

  const [owner, setOwner] = useState({
    name: user?.name || '',
    title: '',
    phone: '',
    email: user?.email || ''
  });

  const [branding, setBranding] = useState({
    logoUrl: '',
    primaryColor: '#16a34a',
    tagline: ''
  });

  const [selectedTier, setSelectedTier] = useState('starter');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/subscription/config`);
      if (res.ok) setConfig(await res.json());
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  };

  const toggleSpecialization = (spec) => {
    setPractice(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const validateStep = () => {
    if (step === 0) {
      if (!practice.name.trim()) { toast.error('Business name is required'); return false; }
      if (!practice.businessType) { toast.error('Please select a business type'); return false; }
    }
    if (step === 1) {
      if (!owner.name.trim()) { toast.error('Owner name is required'); return false; }
      if (!owner.email.trim()) { toast.error('Owner email is required'); return false; }
    }
    return true;
  };

  const nextStep = () => {
    if (!validateStep()) return;
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const prevStep = () => setStep(s => Math.max(s - 1, 0));

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const regData = {
        name: practice.name,
        businessType: practice.businessType,
        description: practice.description,
        branding: {
          logoUrl: branding.logoUrl || null,
          primaryColor: branding.primaryColor,
          tagline: branding.tagline || null
        },
        ownerProfile: { ...owner },
        practice: {
          phone: practice.phone || null,
          website: practice.website || null,
          address: practice.address || null,
          city: practice.city || null,
          state: practice.state || null,
          zip: practice.zip || null,
          specializations: practice.specializations
        }
      };

      const regRes = await registerBusiness(regData);

      const updatedUser = { ...user, isBusinessOwner: true, organizationId: regRes.organization?._id };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      const token = localStorage.getItem('token');
      const checkoutRes = await fetch(`${process.env.REACT_APP_API_URL}/subscription/create-checkout-session`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tierType: 'business', tier: selectedTier })
      });

      if (!checkoutRes.ok) {
        const err = await checkoutRes.json();
        throw new Error(err.error || 'Failed to start checkout');
      }

      const { url } = await checkoutRes.json();
      window.location.href = url;
    } catch (err) {
      console.error('Registration error:', err);
      toast.error(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const tierCards = config?.businessTiers ? [
    { key: 'starter', ...config.businessTiers.starter },
    { key: 'professional', ...config.businessTiers.professional },
    { key: 'enterprise', ...config.businessTiers.enterprise }
  ] : [];

  return (
    <div className="biz-reg">
      <div className="biz-reg-container">
        <div className="biz-reg-header">
          <h1>Register Your Business</h1>
          <p>Set up your organization in a few quick steps</p>
        </div>

        <div className="biz-reg-stepper">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`biz-reg-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
              <span className="biz-reg-step-num">{i < step ? '\u2713' : i + 1}</span>
              <span className="biz-reg-step-label">{s.label}</span>
            </div>
          ))}
        </div>

        <div className="biz-reg-body">
          {step === 0 && (
            <div className="biz-reg-section">
              <h2>Practice Information</h2>
              <p className="biz-reg-hint">Tell us about your business so we can tailor the experience.</p>

              <div className="biz-reg-field">
                <label>Business Name *</label>
                <input type="text" value={practice.name} onChange={e => setPractice({ ...practice, name: e.target.value })} placeholder="e.g. Summit Physical Therapy" />
              </div>

              <div className="biz-reg-field">
                <label>Business Type *</label>
                <select value={practice.businessType} onChange={e => setPractice({ ...practice, businessType: e.target.value })}>
                  <option value="">Select type...</option>
                  {BUSINESS_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div className="biz-reg-field">
                <label>Description</label>
                <textarea value={practice.description} onChange={e => setPractice({ ...practice, description: e.target.value })} placeholder="Brief description of your practice" rows={3} maxLength={500} />
              </div>

              <div className="biz-reg-row">
                <div className="biz-reg-field">
                  <label>Phone</label>
                  <input type="tel" value={practice.phone} onChange={e => setPractice({ ...practice, phone: e.target.value })} placeholder="(555) 123-4567" />
                </div>
                <div className="biz-reg-field">
                  <label>Website</label>
                  <input type="url" value={practice.website} onChange={e => setPractice({ ...practice, website: e.target.value })} placeholder="https://..." />
                </div>
              </div>

              <div className="biz-reg-field">
                <label>Address</label>
                <input type="text" value={practice.address} onChange={e => setPractice({ ...practice, address: e.target.value })} placeholder="123 Main St" />
              </div>

              <div className="biz-reg-row biz-reg-row-3">
                <div className="biz-reg-field">
                  <label>City</label>
                  <input type="text" value={practice.city} onChange={e => setPractice({ ...practice, city: e.target.value })} />
                </div>
                <div className="biz-reg-field">
                  <label>State</label>
                  <input type="text" value={practice.state} onChange={e => setPractice({ ...practice, state: e.target.value })} maxLength={2} placeholder="CA" />
                </div>
                <div className="biz-reg-field">
                  <label>ZIP</label>
                  <input type="text" value={practice.zip} onChange={e => setPractice({ ...practice, zip: e.target.value })} maxLength={10} placeholder="90210" />
                </div>
              </div>

              <div className="biz-reg-field">
                <label>Specializations</label>
                <div className="biz-reg-tags">
                  {SPECIALIZATIONS.map(spec => (
                    <button key={spec} type="button" className={`biz-reg-tag ${practice.specializations.includes(spec) ? 'selected' : ''}`} onClick={() => toggleSpecialization(spec)}>
                      {spec}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="biz-reg-section">
              <h2>Owner Information</h2>
              <p className="biz-reg-hint">This is the primary account holder and billing contact.</p>

              <div className="biz-reg-row">
                <div className="biz-reg-field">
                  <label>Full Name *</label>
                  <input type="text" value={owner.name} onChange={e => setOwner({ ...owner, name: e.target.value })} placeholder="Jane Doe" />
                </div>
                <div className="biz-reg-field">
                  <label>Title / Role</label>
                  <input type="text" value={owner.title} onChange={e => setOwner({ ...owner, title: e.target.value })} placeholder="e.g. Practice Director" />
                </div>
              </div>

              <div className="biz-reg-row">
                <div className="biz-reg-field">
                  <label>Email *</label>
                  <input type="email" value={owner.email} onChange={e => setOwner({ ...owner, email: e.target.value })} placeholder="jane@example.com" />
                </div>
                <div className="biz-reg-field">
                  <label>Phone</label>
                  <input type="tel" value={owner.phone} onChange={e => setOwner({ ...owner, phone: e.target.value })} placeholder="(555) 987-6543" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="biz-reg-section">
              <h2>Branding</h2>
              <p className="biz-reg-hint">Customize how your organization appears to clients. You can update these later from your dashboard.</p>

              <div className="biz-reg-field">
                <label>Logo URL</label>
                <input type="url" value={branding.logoUrl} onChange={e => setBranding({ ...branding, logoUrl: e.target.value })} placeholder="https://example.com/logo.png" />
                {branding.logoUrl && (
                  <div className="biz-reg-logo-preview">
                    <img src={branding.logoUrl} alt="Logo preview" onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>

              <div className="biz-reg-field">
                <label>Brand Color</label>
                <div className="biz-reg-color-row">
                  <input type="color" value={branding.primaryColor} onChange={e => setBranding({ ...branding, primaryColor: e.target.value })} />
                  <input type="text" value={branding.primaryColor} onChange={e => setBranding({ ...branding, primaryColor: e.target.value })} placeholder="#16a34a" maxLength={7} className="biz-reg-color-text" />
                </div>
              </div>

              <div className="biz-reg-field">
                <label>Tagline</label>
                <input type="text" value={branding.tagline} onChange={e => setBranding({ ...branding, tagline: e.target.value })} placeholder="e.g. Helping you move better, feel better" maxLength={200} />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="biz-reg-section">
              <h2>Choose Your Plan</h2>
              <p className="biz-reg-hint">Select a plan to get started. The Starter plan includes a 14-day free trial.</p>

              <div className="biz-reg-plans">
                {tierCards.map(tier => {
                  const price = (tier.price / 100).toFixed(0);
                  const isTrial = tier.trialDays > 0;
                  return (
                    <div
                      key={tier.key}
                      className={`biz-reg-plan-card ${selectedTier === tier.key ? 'selected' : ''} ${tier.key === 'professional' ? 'popular' : ''}`}
                      onClick={() => setSelectedTier(tier.key)}
                    >
                      {isTrial && <div className="biz-reg-trial-badge">{tier.trialDays}-day free trial</div>}
                      {tier.key === 'professional' && <div className="biz-reg-popular-badge">Best Value</div>}
                      <h3>{tier.name}</h3>
                      <div className="biz-reg-plan-price">
                        <span className="biz-reg-price">${price}</span>
                        <span className="biz-reg-interval">/month</span>
                      </div>
                      <p className="biz-reg-plan-desc">{tier.description}</p>
                      <ul className="biz-reg-plan-features">
                        {tier.features.map((f, i) => <li key={i}><i className="fas fa-check" /> {f}</li>)}
                      </ul>
                      <div className="biz-reg-plan-radio">
                        <span className={`biz-reg-radio ${selectedTier === tier.key ? 'checked' : ''}`} />
                        <span>{selectedTier === tier.key ? 'Selected' : 'Select'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="biz-reg-nav">
          {step > 0 && (
            <button type="button" className="biz-reg-btn biz-reg-btn-secondary" onClick={prevStep} disabled={loading}>
              Back
            </button>
          )}
          <div className="biz-reg-nav-spacer" />
          {step < STEPS.length - 1 ? (
            <button type="button" className="biz-reg-btn biz-reg-btn-primary" onClick={nextStep}>
              Continue
            </button>
          ) : (
            <button type="button" className="biz-reg-btn biz-reg-btn-primary" onClick={handleSubscribe} disabled={loading}>
              {loading ? 'Setting up...' : selectedTier === 'starter' ? 'Start Free Trial' : 'Subscribe & Get Started'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
